import { map, Observable } from 'rxjs';

// IMPORTANT Autogenerated file
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssetDto,
  PreScheduledMaintenanceFiltersDto,
  PreTestingAssetDto,
  ScheduledMaintenanceGenerateDto,
  ScheduledMaintenancesPreviewDto,
  TestingDto,
} from '@ellf/common-dto';
import {
  DownloadExcelType,
  FieldConfig,
  FilterValue,
} from '@ellf/common-interfaces';
import { ApiClientConfig } from '../api-client-config';
import { BaseCRUDApiClient } from './baseCRUD.api-client';

@Injectable()
export class AssetApiClient extends BaseCRUDApiClient<AssetDto> {
  baseUrl = this.config.apiEndpoint + '/asset';

  constructor(http: HttpClient, config: ApiClientConfig) {
    super(http, config);
  }

  getBySupplierId(supplierId: string) {
    return this.http.get<AssetDto[]>(
      `${this.baseUrl}/bySupplierId/${supplierId}`,
    );
  }

  getHierarchy(assetId: string) {
    return this.http.get<AssetDto>(`${this.baseUrl}/${assetId}/hierarchy`);
  }

  addFromPretesting(entity: Partial<PreTestingAssetDto>) {
    return this.http.post<AssetDto>(
      `${this.baseUrl}/create-asset-from-pre-testing`,
      entity,
    );
  }

  generateTimeSchedulePreview(filters: PreScheduledMaintenanceFiltersDto) {
    return this.http.post<{
      timeSchedulePreview: ScheduledMaintenancesPreviewDto;
      totalAssets: number;
    }>(`${this.baseUrl}/generate-time-schedule-preview`, filters);
  }

  generateTimeSchedule(items: ScheduledMaintenanceGenerateDto[]) {
    return this.http.post<ScheduledMaintenanceGenerateDto>(
      `${this.baseUrl}/generate-time-schedule`,
      items,
    );
  }

  generateLabel(id: string) {
    return this.http.get<AssetDto>(`${this.baseUrl}/${id}/label`);
  }

  uploadAssetPeriodicity(list: FileList) {
    const files = Array.from(list);
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    return this.http.post<
      { errors: { error: string; row: number | undefined }[] }[]
    >(this.baseUrl + '/upload-periodicity-excel', fd);
  }

  downloadPeriodicityModelExcel(
    contractId: string | number,
    fileName?: string,
  ): Observable<ArrayBuffer> {
    return this.http
      .post(
        this.baseUrl + '/download-periodicity-model-excel',
        { fileName, contractId },
        {
          responseType: 'arraybuffer',
        },
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        }),
      );
  }

  override downloadExcel(
    fileName?: string | undefined,
    columns?: FieldConfig[] | undefined,
    filters?: FilterValue[] | undefined,
    orderBy?: { [field: string]: 'asc' | 'desc' } | undefined,
    join?: string[] | undefined,
    downloadExcelType?: any, //DownloadExcelType
  ): Observable<ArrayBuffer> {
    return super.downloadExcel(
      fileName,
      columns,
      filters,
      orderBy,
      join,
      downloadExcelType === DownloadExcelType.accreditation
        ? '/download-accreditation-excel'
        : '/download-excel',
    );
  }

  downloadExcelSimplified(
    filters?: FilterValue[] | undefined,
    fileName?: string | undefined,
  ): Observable<ArrayBuffer> {
    return this.http.post(
      this.baseUrl + '/download-simplified-excel',
      { fileName, filters: filters || [] },
      {
        responseType: 'arraybuffer',
      },
    );
  }

  downloadAssetsModelExcel(
    contractNumber: string | number,
    fileName?: string,
  ): Observable<ArrayBuffer> {
    return this.http
      .post(
        this.baseUrl + '/download-assets-model-excel',
        { fileName, contractNumber },
        {
          responseType: 'arraybuffer',
        },
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        }),
      );
  }
  uploadAssets(list: FileList) {
    const files = Array.from(list);
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    return this.http.post<
      { errors: { error: string; row: number | undefined }[] }[]
    >(this.baseUrl + '/upload-assets-excel', fd);
  }

  assetsForTraining() {
    return this.http.get<AssetDto[]>(`${this.baseUrl}/assets-for-training`);
  }

  duplicateAsset(
    id: string,
  ): Observable<{ testing: TestingDto; asset: AssetDto }> {
    return this.http.post<{ testing: TestingDto; asset: AssetDto }>(
      `${this.baseUrl}/duplicate-testing-asset`,
      {
        id,
      },
    );
  }
}
